import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, WifiOff } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { getDeviceConsoleWsUrl } from '../../config/api'

const MAX_ROWS = 500

export default function DeviceMqttConsole() {
  const { t } = useTranslation()
  const { deviceId } = useParams()
  const navigate = useNavigate()
  const wsRef = useRef(null)
  const scrollRef = useRef(null)
  const [isPopup] = useState(
    () => typeof window !== 'undefined' && Boolean(window.opener)
  )
  const [mode, setMode] = useState('organized')
  const [state, setState] = useState('connecting')
  const [errorText, setErrorText] = useState('')
  const [rows, setRows] = useState([])

  useEffect(() => {
    if (!deviceId) return undefined
    let stopped = false
    let reconnectTimer = null

    const connect = () => {
      if (stopped) return
      setState('connecting')
      setErrorText('')
      let ws
      try {
        ws = new WebSocket(getDeviceConsoleWsUrl(deviceId))
      } catch (e) {
        setState('error')
        setErrorText(e?.message || t('devices.console.wsOpenFailed'))
        return
      }
      wsRef.current = ws

      ws.onopen = () => {
        if (stopped) return
        setState('open')
      }

      ws.onmessage = (evt) => {
        try {
          const payload = JSON.parse(String(evt.data || '{}'))
          const row = {
            receivedAt: payload.receivedAt || new Date().toISOString(),
            topic: payload.topic || '',
            raw: payload.raw || '',
            data: payload.data && typeof payload.data === 'object' ? payload.data : null,
          }
          setRows((prev) => {
            const next = [...prev, row]
            if (next.length > MAX_ROWS) {
              next.splice(0, next.length - MAX_ROWS)
            }
            return next
          })
        } catch {
          // ignora frame inválido
        }
      }

      ws.onerror = () => {
        if (stopped) return
        setState('error')
      }

      ws.onclose = (evt) => {
        if (stopped) return
        setState('closed')
        if (evt?.reason) setErrorText(evt.reason)
        reconnectTimer = window.setTimeout(connect, 2000)
      }
    }

    connect()

    return () => {
      stopped = true
      if (reconnectTimer) window.clearTimeout(reconnectTimer)
      try {
        wsRef.current?.close(1000, 'leaving')
      } catch {
        // ignore
      }
    }
  }, [deviceId, t])

  const statusBadge = useMemo(() => {
    if (state === 'open') return { text: t('common.status.connected'), cls: 'badge-success' }
    if (state === 'connecting') return { text: t('devices.console.connecting'), cls: 'badge-warning' }
    if (state === 'error') return { text: t('common.status.error'), cls: 'badge-error' }
    return { text: t('devices.console.disconnected'), cls: 'badge-gray' }
  }, [state, t])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [rows, mode])

  return (
    <div className="h-screen w-screen bg-gray-950 text-gray-100 flex flex-col">
      <div className="border-b border-gray-800 px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            className="btn btn-outline !text-xs !px-2 !py-1"
            onClick={() => {
              if (isPopup) window.close()
              else navigate('/devices')
            }}
            title={isPopup ? t('devices.console.closeWindow') : t('devices.console.backToList')}
          >
            <ArrowLeft className="w-4 h-4" />
            {isPopup ? t('common.buttons.close') : t('common.buttons.back')}
          </button>
          <div className="min-w-0">
            <div className="font-semibold truncate">{t('devices.console.titleLong')}</div>
            <div className="text-xs text-gray-400 truncate">DeviceId: {deviceId}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={clsx('badge', statusBadge.cls)}>{statusBadge.text}</span>
          <button
            type="button"
            className={clsx('btn btn-outline !text-xs !px-2 !py-1', mode === 'organized' && '!bg-gray-800')}
            onClick={() => setMode('organized')}
          >
            {t('devices.console.modeOrganized')}
          </button>
          <button
            type="button"
            className={clsx('btn btn-outline !text-xs !px-2 !py-1', mode === 'raw' && '!bg-gray-800')}
            onClick={() => setMode('raw')}
          >
            {t('devices.console.modeRaw')}
          </button>
        </div>
      </div>

      {errorText ? (
        <div className="px-4 py-2 text-xs text-yellow-300 border-b border-yellow-800/40 bg-yellow-950/20">
          {errorText}
        </div>
      ) : null}

      <div ref={scrollRef} className="flex-1 overflow-auto p-3 font-mono text-xs">
        {rows.length === 0 ? (
          <div className="h-full flex items-center justify-center text-gray-500">
            <WifiOff className="w-4 h-4 mr-2" />
            {t('devices.console.waiting')}
          </div>
        ) : null}

        {rows.length > 0 && mode === 'raw' && (
          <table className="table-auto w-max min-w-full border border-gray-800 bg-black/40">
            <thead className="bg-gray-900/80 text-gray-300">
              <tr>
                <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">receivedAt</th>
                <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">topic</th>
                <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">raw</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={`raw-${idx}`} className="border-t border-gray-800 align-top">
                  <td className="px-2 py-1 whitespace-nowrap text-gray-400">{r.receivedAt}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-gray-300">{r.topic || '—'}</td>
                  <td className="px-2 py-1 text-gray-100">
                    <pre className="whitespace-pre-wrap break-all">{r.raw || '—'}</pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {rows.length > 0 && mode === 'organized' && (
          <table className="table-auto w-max min-w-full border border-gray-800 bg-black/40">
            <thead className="bg-gray-900/80 text-gray-300">
              <tr>
                <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">receivedAt</th>
                <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">topic</th>
                <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">ts</th>
                <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">chipId</th>
                <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">deviceId</th>
                <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">slaveId</th>
                <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">status</th>
                <th className="px-2 py-1 text-left font-semibold whitespace-nowrap">rssi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const d = r.data || {}
                return (
                  <tr key={`org-${idx}`} className="border-t border-gray-800">
                    <td className="px-2 py-1 whitespace-nowrap text-gray-400">{r.receivedAt}</td>
                    <td className="px-2 py-1 whitespace-nowrap text-gray-300">{r.topic || '—'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmt(d.ts)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmt(d.chipId)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmt(d.deviceId)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmt(d.slaveId)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmt(d.status)}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmt(d.rssi)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function fmt(v) {
  return v == null || v === '' ? '—' : String(v)
}
